import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { db } from '../lib/firebase';
import { doc, getDoc, addDoc, collection, Timestamp } from 'firebase/firestore';

interface EventData {
    id: string;
    title: string;
    date: string;
    games: string[];
    location: string;
    startTime: string;
    endTime: string;
    coach?: string;
}

const gameLabels: Record<string, string> = {
    'smash-bros-solo-hs': 'Super Smash Bros. Solo (HS)',
    'smash-bros-solo-ms': 'Super Smash Bros. Solo (MS)',
    'smash-bros-crews': 'Super Smash Bros. Crews',
    'madden': 'Madden NFL 26',
    'fortnite-duos': 'Fortnite Duos',
    'rocket-league': 'Rocket League',
    'mario-kart': 'Mario Kart 8 Deluxe',
    'playoffs': 'Playoffs',
    'state-finals': 'State Finals',
};

export default function CheckinPage() {
    const { eventId } = useParams<{ eventId: string }>();
    const [event, setEvent] = useState<EventData | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [selectedGame, setSelectedGame] = useState('');

    // Submission state
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);

    // Load event
    useEffect(() => {
        async function loadData() {
            if (!eventId) return;

            try {
                const eventDoc = await getDoc(doc(db, 'events', eventId));
                if (!eventDoc.exists()) {
                    setError('Event not found');
                    setLoading(false);
                    return;
                }
                setEvent({ id: eventDoc.id, ...eventDoc.data() } as EventData);
                setLoading(false);
            } catch (err) {
                console.error('Error loading data:', err);
                setError('Failed to load event');
                setLoading(false);
            }
        }
        loadData();
    }, [eventId]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!eventId || !firstName || !lastName || !selectedGame) return;

        setSubmitting(true);

        try {
            await addDoc(collection(db, 'checkins'), {
                eventId,
                studentName: `${firstName} ${lastName}`,
                selectedGame,
                checkinTime: Timestamp.now(),
                checkedInBy: 'kiosk',
            });

            setSuccess(true);

            // Reset after 3 seconds
            setTimeout(() => {
                setSuccess(false);
                setFirstName('');
                setLastName('');
                setSelectedGame('');
            }, 3000);
        } catch (err) {
            console.error('Check-in failed:', err);
            setError('Check-in failed. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-secondary-900">
                <div className="text-center">
                    <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-secondary-400">Loading...</p>
                </div>
            </div>
        );
    }

    if (error || !event) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-secondary-900 p-4">
                <div className="card text-center max-w-md">
                    <div className="text-6xl mb-4">❌</div>
                    <h1 className="text-2xl font-bold text-white mb-2">Error</h1>
                    <p className="text-secondary-400 mb-6">{error || 'Event not found'}</p>
                    <Link to="/" className="btn-primary">Go Home</Link>
                </div>
            </div>
        );
    }

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-secondary-900 p-4">
                <div className="card text-center max-w-md animate-fade-in">
                    <div className="text-6xl mb-4">✅</div>
                    <h1 className="text-3xl font-display font-bold text-white mb-2">Checked In!</h1>
                    <p className="text-secondary-400 mb-2">Welcome, <span className="text-primary-500">{firstName} {lastName}</span></p>
                    <p className="text-secondary-500 text-sm">Playing: {gameLabels[selectedGame] || selectedGame}</p>
                    <p className="text-secondary-600 text-sm mt-4">Returning to check-in...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-secondary-900 p-4">
            <div className="max-w-md mx-auto">
                {/* Header */}
                <div className="text-center mb-8 animate-fade-in">
                    <div className="w-16 h-16 bg-primary-500 rounded-xl flex items-center justify-center mx-auto mb-4">
                        <span className="text-3xl">🏴‍☠️</span>
                    </div>
                    <h1 className="text-2xl font-display font-bold text-white mb-1">Peck Pirates</h1>
                    <h2 className="text-xl text-primary-500 mb-4">{event.title}</h2>
                    <p className="text-secondary-400">{event.date} • {event.startTime} - {event.endTime}</p>
                    <p className="text-secondary-500 text-sm">{event.location}</p>
                </div>

                {/* Check-in Form */}
                <form onSubmit={handleSubmit} className="card animate-slide-up">
                    <h3 className="text-lg font-semibold text-white mb-4">Check In</h3>

                    {/* Name Inputs */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                        <div>
                            <label className="label">First Name</label>
                            <input
                                type="text"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                placeholder="First"
                                className="input"
                                required
                            />
                        </div>
                        <div>
                            <label className="label">Last Name</label>
                            <input
                                type="text"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                placeholder="Last"
                                className="input"
                                required
                            />
                        </div>
                    </div>

                    {/* Game Selection */}
                    <div className="mb-6">
                        <label className="label">Select Your Game</label>
                        <div className="grid gap-2">
                            {event.games.map(game => (
                                <label
                                    key={game}
                                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${selectedGame === game
                                            ? 'border-primary-500 bg-primary-500/10'
                                            : 'border-secondary-600 hover:border-secondary-500'
                                        }`}
                                >
                                    <input
                                        type="radio"
                                        name="game"
                                        value={game}
                                        checked={selectedGame === game}
                                        onChange={(e) => setSelectedGame(e.target.value)}
                                        className="sr-only"
                                    />
                                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedGame === game
                                            ? 'border-primary-500 bg-primary-500'
                                            : 'border-secondary-500'
                                        }`}>
                                        {selectedGame === game && (
                                            <div className="w-2 h-2 rounded-full bg-white"></div>
                                        )}
                                    </div>
                                    <span className="text-white">{gameLabels[game] || game}</span>
                                </label>
                            ))}
                        </div>
                    </div>

                    {/* Submit */}
                    <button
                        type="submit"
                        disabled={submitting || !firstName || !lastName || !selectedGame}
                        className="btn-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {submitting ? 'Checking in...' : 'Check In'}
                    </button>
                </form>

                {/* Contact */}
                <div className="text-center text-secondary-500 text-sm mt-6">
                    <p>Questions? Contact Coach Anklam</p>
                    <p>anklamb@peckschools.net • (810) 378-0645</p>
                </div>
            </div>
        </div>
    );
}
